import React, { useState, useCallback } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import { useServiceContext } from '../../root'
import { useTranslation } from 'react-i18next'
import { useModal } from '../../contexts/ModalContext'
import useChecksumService from '../../hooks/useChecksumService'

import MultiFilePreviewModal from '../MultiFilePreviewModal'

// TODO: Import AclLevel from constants if available
const AclLevel = {
  BROWSE: 'BROWSE',
  NONE: 'NONE'
} as const

const revertCommentModalClasses = {
  modalClass: 'level-one-modal',
  backDropClass: 'level-one-backdrop'
}

export interface FileUploadModalProps {
  fileToUpload: any // File | null, but may have extra metadata
  setFileToUpload: (file: any) => void
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({ fileToUpload, setFileToUpload }) => {
  const context = useServiceContext()
  const { t } = useTranslation()
  const { showModal } = useModal()
  const { sha1_hash, getFileSizeLimit } = useChecksumService()
  const excerpt = useCallback((text: string, _stripHtml: boolean, length: number) => {
    if (!text) return ''
    return text.length > length ? text.substring(0, length) + '...' : text
  }, [])
  const [inputKey, setInputKey] = useState(Date.now())

  // Show MultiFilePreviewModal using showModal
  const preview = (file: any) => {
    const copyOfItems = cloneDeep(file?.checksum?.existFiles)
    const outputFiles = (copyOfItems || []).reduce((existFiles: any[], f: any) => {
      const filePath = existFiles.find((ef) => ef.fileFullPath === f.fileFullPath)
      if (!filePath) existFiles.push(f)
      return existFiles
    }, [])
    showModal(<MultiFilePreviewModal fileId={outputFiles} modalClasses={revertCommentModalClasses} />, {
      keepParentModalsOpen: true
    })
  }

  // Deduplication logic: compare newFile to all existing files
  const isDuplicate = (newFile: any, existingFiles: any[] = []) => {
    return existingFiles.some((f) => f?.name === newFile.name && f?.size === newFile.size)
  }

  // Add file metadata and checksum
  const fileAddedHandler = async (filesFromUpload: any[]) => {
    const files = filesFromUpload.map((file) => {
      file.relativePath = file.webkitRelativePath || file.name
      return file
    })
    const dataFileSizeLimit = await getFileSizeLimit()
    await sha1_hash(files, dataFileSizeLimit).then((data: any[]) => {
      filesFromUpload.forEach((itemChecksum) => {
        files.forEach((addedFile) => {
          if (addedFile.name === itemChecksum.name) {
            const fs = data.filter((d) => d.fileName === itemChecksum.name)
            if (!fs[0]?.checksumValidate) {
              itemChecksum.checksum = fs[0] ? fs[0] : null
            } else {
              itemChecksum.checksumValidate = fs[0]?.checksumValidate
            }
          }
        })
      })
    })
    return filesFromUpload
  }

  // Process files: deduplicate, add metadata, set in parent
  const processFiles = async (files: any[]) => {
    // Only allow one file in this modal, but keep logic for future multi-file
    const addedFiles = files
    const uniqueNewFiles = addedFiles.filter((file) => !isDuplicate(file, fileToUpload ? [fileToUpload] : []))
    if (uniqueNewFiles.length > 0) {
      const filesWithMetadata = await fileAddedHandler(uniqueNewFiles)
      console.log('filesWithMetadata')
      console.log(filesWithMetadata)
      setFileToUpload(filesWithMetadata[0])
    }
  }

  // Handle file input change
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesFromInput = Array.from(e.target.files || [])
    if (filesFromInput.length > 0) {
      console.log('filesFromInput')
      console.log(filesFromInput)
      await processFiles(filesFromInput)
      setInputKey(Date.now())
    }
  }

  return (
    <>
      <input
        key={inputKey}
        onChange={handleFileChange}
        type="file"
        name={'files'}
        id="documents"
        className="visibility-h pos-absolute"
      />
      <div className={`transfers-wrapper`}>
        <div className="d-inline-flex">
          <div>
            <label
              className="ellipsis btn btn-md btn-upload btn-chita btn-second mb20 text-transform-none d-flex-important"
              htmlFor="file-upload-input"
              title={fileToUpload?.name}
            >
              <span className={`${fileToUpload?.name ? 'upload-browse-text file-name' : ''}`}>
                {fileToUpload?.name ? excerpt(fileToUpload.name, false, 29) : t('BROWSER_BROWSE_FILE')}
              </span>
              <input
                key={inputKey}
                onChange={handleFileChange}
                type="file"
                name={'files'}
                id="file-upload-input"
                className="form-control visibility-h pos-absolute"
              />
            </label>
          </div>
          <>
            {fileToUpload?.checksumValidate && (
              <div
                style={{ marginLeft: '5px' }}
                className="d-inline-flex btn btn-md btn-upload btn-chita btn-second mb20 text-transform-none"
              >
                <div>
                  <strong>{t('DUPLICATE_DETECTION_FAILED')}</strong>
                </div>
                <div>
                  <p>{fileToUpload.checksumValidate}</p>
                </div>
                <div>
                  <span className="warning-file-upload"></span>
                </div>
              </div>
            )}
            {fileToUpload?.checksum?.existFiles && fileToUpload?.checksum?.existFiles.length > 0 && (
              <div className="ml5">
                <div className="btn btn-md btn-upload btn-chita btn-second mb20 text-transform-none d-flex-important">
                  <div>
                    <strong>{t('DUPLICATE_DETECTED')}</strong>
                  </div>
                  {fileToUpload.checksum.existFiles.length > 1 && (
                    <div>
                      <a
                        id="561a107d-6825-4860-9b16-77b7d39fddeb"
                        onClick={() => preview(fileToUpload)}
                        className="cursor-pointer"
                      >
                        {t('WORKFLOW_MULTIPLE_DOCS')}
                      </a>
                    </div>
                  )}
                  {fileToUpload?.checksum?.existFiles.length === 1 && (
                    <div>
                      {fileToUpload.checksum.existFiles[0].level !== AclLevel.BROWSE &&
                        fileToUpload.checksum.existFiles[0].level !== AclLevel.NONE && (
                          <a
                            id="505b8c54-c612-4a9c-9319-74a0c1ea94b2"
                            className={`hover cursor-pointer ${fileToUpload.checksum.existFiles[0].fileName.length > 28 ? 'trunk-link' : ''}`}
                            style={{ width: '156px' }}
                            onClick={() => preview(fileToUpload)}
                            title={fileToUpload.checksum.existFiles[0].fileName}
                          >
                            {fileToUpload.checksum.existFiles[0].fileName}
                          </a>
                        )}
                      {fileToUpload.checksum.existFiles[0].level === AclLevel.BROWSE && (
                        <p
                          id="79d459b0-1932-4296-aa10-b82eeaafe5cb"
                          className={`hover ${fileToUpload.checksum.existFiles[0].fileName.length > 28 ? 'trunk-link' : ''}`}
                          style={{ width: '156px' }}
                          title={fileToUpload.checksum.existFiles[0].fileName}
                        >
                          {excerpt(fileToUpload.checksum.existFiles[0].fileName, false, 29)}
                        </p>
                      )}
                      {fileToUpload.checksum.existFiles[0].level === AclLevel.NONE && (
                        <p>{t('INSUFFICIENT_ACCESS_RIGHTS')}</p>
                      )}
                    </div>
                  )}
                  <div>
                    <span className="warning-file-upload warning-icon mt20"></span>
                  </div>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </>
  )
}

export default FileUploadModal
